import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface Usuario {
  id: string;
  login: string;
  nome: string | null;
  senha: string;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

const usuarioSchema = z.object({
  login: z.string().min(3, 'O login deve ter no mínimo 3 caracteres'),
  senha: z.string().min(6, 'A senha deve ter no mínimo 6 caracteres').optional(),
  nome: z.string().optional(),
  ativo: z.boolean().default(true),
});

type UsuarioFormData = z.infer<typeof usuarioSchema>;

export const UsuarioForm = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user: currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [usuario, setUsuario] = useState<Usuario | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<UsuarioFormData>({
    resolver: zodResolver(usuarioSchema),
    defaultValues: {
      ativo: true
    }
  });

  useEffect(() => {
    if (id) {
      fetchUsuario(id);
    }
  }, [id]);

  const fetchUsuario = async (usuarioId: string) => {
    setIsLoading(true);
    try {
      const response = await api.get<Usuario>(`/api/usuarios/${usuarioId}`);
      const data = response.data;
      if (!data) throw new Error('Usuário não encontrado ou resposta inválida');
      
      setIsAdmin(data.login === 'admin');
      
      if (data.login === 'admin' && currentUser?.login !== 'admin') {
        addToast({
          type: 'error',
          title: 'Apenas o próprio usuário admin pode editar seu perfil'
        });
        navigate('/usuarios');
        return;
      }

      setUsuario(data);
      reset({
        login: data.login,
        nome: data.nome || '',
        ativo: data.ativo,
      });
    } catch (error: any) {
      console.error('Erro ao buscar usuário:', error);
      addToast({
        type: 'error',
        title: 'Erro ao carregar usuário',
        description: error.response?.data?.error || error.message
      });
      navigate('/usuarios');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: UsuarioFormData) => {
    if (isAdmin && currentUser?.login !== 'admin') {
      addToast({
        type: 'error',
        title: 'Apenas o próprio usuário admin pode editar seu perfil'
      });
      return;
    }

    const url = id ? `/api/usuarios/${id}` : '/api/usuarios';
    const method = id ? 'put' : 'post';
    
    const payload = { ...data };
    if (id && !payload.senha) {
      delete payload.senha;
    }

    try {
      let response;
      if (method === 'put') {
        response = await api.put(url, payload);
      } else {
        response = await api.post(url, payload);
      }

      if (response.status === 200 || response.status === 201) {
        addToast({
          type: 'success',
          title: `Usuário ${id ? 'atualizado' : 'criado'} com sucesso!`
        });
        navigate('/usuarios');
      } else {
        console.error('Falha ao salvar usuário, status:', response.status, response.data);
        const errorDesc = response.data?.error || `Status: ${response.status}`;
        addToast({ type: 'error', title: 'Erro ao salvar usuário', description: errorDesc });
        throw new Error(errorDesc || 'Falha ao salvar usuário');
      }
    } catch (error: any) {
      console.error('Erro ao salvar usuário:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido ao salvar.';
      addToast({
        type: 'error',
        title: 'Erro ao salvar usuário',
        description: errorMessage
      });
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/usuarios')}
          className="mr-4 p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold">{id ? 'Editar Usuário' : 'Novo Usuário'}</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="login" className="block text-sm font-medium text-gray-700">
            Login <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="login"
            {...register('login')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#8B008B] focus:ring-[#8B008B] sm:text-sm"
            disabled={isAdmin}
          />
          {errors.login && (
            <p className="mt-1 text-sm text-red-600">{errors.login.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="nome" className="block text-sm font-medium text-gray-700">
            Nome
          </label>
          <input
            type="text"
            id="nome"
            {...register('nome')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#8B008B] focus:ring-[#8B008B] sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="senha" className="block text-sm font-medium text-gray-700">
            {id ? 'Nova Senha' : 'Senha'} {!id && <span className="text-red-500">*</span>}
          </label>
          <input
            type="password"
            id="senha"
            {...register('senha')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#8B008B] focus:ring-[#8B008B] sm:text-sm"
          />
          {errors.senha && (
            <p className="mt-1 text-sm text-red-600">{errors.senha.message}</p>
          )}
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="ativo"
            {...register('ativo')}
            className="h-4 w-4 text-[#8B008B] focus:ring-[#8B008B] border-gray-300 rounded"
          />
          <label htmlFor="ativo" className="ml-2 block text-sm text-gray-900">
            Ativo
          </label>
        </div>

        {usuario && (
          <div className="flex justify-between items-center border-t pt-4 mt-4">
            <div className="text-right">
              <label className="block text-sm font-medium text-gray-700 mb-1">Data de Cadastro</label>
              <span className="text-sm text-gray-600">
                {new Date(usuario.createdAt).toLocaleString('pt-BR')}
              </span>
            </div>
            <div className="text-right">
              <label className="block text-sm font-medium text-gray-700 mb-1">Última Atualização</label>
              <span className="text-sm text-gray-600">
                {new Date(usuario.updatedAt).toLocaleString('pt-BR')}
              </span>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/usuarios')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#8B008B]"
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
}; 